export type SelectOption = { label: string; value: string };

export interface VariantColumnConfig {
  // Column header shown above the extra dropdown (e.g. "Supplier", "Drive Stage").
  label: string;
  options: SelectOption[];
  // Category options available for a row, keyed by that row's selected variant value.
  categoriesByOption: Record<string, SelectOption[]>;
}

export interface SectionCategoryConfig {
  // Static Category options for the section. Ignored when `variantColumn`
  // is set — in that case Category options come from `variantColumn.categoriesByOption`.
  categories: SelectOption[];
  subCategoriesByCategory: Record<string, SelectOption[]>;
  // When set, the section also shows an extra dropdown column (Supplier,
  // Drive Stage, etc.) whose selected value drives which Category options
  // are available for that row.
  variantColumn?: VariantColumnConfig;
  // When true, the section's table omits the Category and Sub-category
  // columns entirely (rather than falling back to free-text input for them).
  hideCategoryColumns?: boolean;
}

const toOptions = (values: string[]): SelectOption[] => values.map((value) => ({ label: value, value }));

// Category (and, per category, Sub-category) options are specific to each
// accordion section on the Takeoff Configuration page — sections not listed
// here fall back to free-text input for both fields.
export const SECTION_CATEGORY_CONFIG: Record<string, SectionCategoryConfig> = {
  "Cut & Fill": {
    categories: toOptions(["Site Clearance", "Cut & Fill"]),
    subCategoriesByCategory: {
      "Cut & Fill": toOptions(["Founds", "Paths", "Garden", "Drives"]),
    },
  },
  Foundations: {
    categories: toOptions(["EXCAVATE & CONCRETE", "SUBSTRUCTURE", "ABNORMALS", "FLOORS"]),
    subCategoriesByCategory: {},
  },
  "Plot Paving": {
    categories: toOptions(["Paving Paths", "Macadam Paths"]),
    subCategoriesByCategory: {},
  },
  "Scaffold Platforms": {
    categories: toOptions(["Scaffold Platforms", "Loading Bays", "Stair Access"]),
    subCategoriesByCategory: {},
  },
  "Ret & Screen Walls": {
    categories: toOptions(["Ret & Screen Take off"]),
    subCategoriesByCategory: {
      "Ret & Screen Take off": toOptions(["Garden Retaining Wall", "Drive Retaining Walls", "Concrete Core Walls"]),
    },
  },
  "Geowall & Tobermore Walls": {
    categories: toOptions(["Geowalls", "Tobermore Walls"]),
    subCategoriesByCategory: {},
  },
  Garden: {
    categories: [],
    subCategoriesByCategory: {},
    hideCategoryColumns: true,
  },
  Drainage: {
    categories: [],
    subCategoriesByCategory: {},
    variantColumn: {
      label: "Supplier",
      options: toOptions(["Plot drainage - OSMA", "Plot drainage - POLYPIPE"]),
      categoriesByOption: {
        "Plot drainage - OSMA": toOptions(["OSMA - FOUL", "OSMA - STORM"]),
        "Plot drainage - POLYPIPE": toOptions(["POLYPIPE - FOUL", "POLYPIPE - STORM"]),
      },
    },
  },
  "Plot Drives": {
    categories: [],
    subCategoriesByCategory: {},
    variantColumn: {
      label: "Drive Stage",
      options: toOptions(["Drives to Binder", "Drives Surface Course"]),
      categoriesByOption: {
        "Drives to Binder": toOptions([
          "Private Macadam Drive",
          "Private Block Paved Drive",
          "Private Permeable Paving",
          "Shared Macadam Drive",
          "Shared Block Paved Drive",
          "Shared Permeable Paving",
          "Kerbs and Edgings",
        ]),
        "Drives Surface Course": toOptions([
          "Private Macadam Drive",
          "Private Block Paved Drive",
          "Private Permeable Paving",
          "Shared Macadam Drive",
          "Shared Block Paving",
          "Shared Permeable Paving",
          "Ancillary Items",
        ]),
      },
    },
  },
};
